import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ProviderLogo } from "@/components/ProviderLogo/ProviderLogo";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { CatalogEntry, IntegrationStatus } from "../constants/catalog";
import { useProviderConnectionStatus } from "../hooks/useProviderConnectionStatus";

interface IntegrationCardProps {
	entry: CatalogEntry;
}

const STATUS_CLASSES: Record<IntegrationStatus, string> = {
	connected: "text-green-600 border-green-200 bg-green-50",
	notConnected: "text-text-meta border-border-light bg-brand-white",
	comingSoon: "text-text-meta border-border-light bg-brand-white",
	available: "text-brand-light border-brand-light bg-brand-white",
};

export const IntegrationCard: FunctionComponent<IntegrationCardProps> = ({ entry }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const isConnected = useProviderConnectionStatus(entry.id);

	const status: IntegrationStatus = useMemo(() => {
		if (entry.docs || entry.manage) {
			return "available";
		}

		if (entry.comingSoon) {
			return "comingSoon";
		}

		return isConnected ? "connected" : "notConnected";
	}, [entry.docs, entry.manage, entry.comingSoon, isConnected]);

	const ctaLabelKey = entry.docs
		? "integrations.cta.setupGuide"
		: entry.manage || isConnected
			? "integrations.cta.manage"
			: "integrations.cta.connect";

	const handleClick = () => {
		if (entry.route) {
			navigate(entry.route);
		}
	};

	return (
		<div className="flex flex-col gap-4 p-5 bg-brand-white border border-border-light rounded-xl">
			<div className="flex items-start justify-between gap-3">
				<ProviderLogo provider={entry.id} size={40} />
				<div className="flex items-center gap-2">
					{entry.beta && (
						<Tooltip content={t("integrations.beta.tooltip")}>
							<span className="text-xs font-medium px-2 py-1 rounded-full border text-text-warning border-status-warning-light bg-background-warning">
								{t("integrations.beta.label")}
							</span>
						</Tooltip>
					)}
					<span
						className={`text-xs font-medium px-2 py-1 rounded-full border ${STATUS_CLASSES[status]}`}
					>
						{t(`integrations.status.${status}`)}
					</span>
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<h3 className="text-text font-medium">{t(entry.titleKey)}</h3>
				<div className="flex flex-row justify-between items-center">
					<p className="text-text-meta text-sm">{t(entry.descriptionKey)}</p>
					<div className="mt-auto w-fit">
						<Button
							color={
								isConnected || entry.docs || entry.manage
									? ButtonColor.ACTION_SECONDARY
									: ButtonColor.ACTION
							}
							disabled={entry.comingSoon || !entry.route}
							size={ButtonSize.S}
							testId={`integration-card-${entry.id}`}
							title={t(ctaLabelKey)}
							type="button"
							onClick={handleClick}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
